import React from 'react';
import { useCanvas } from '../../hooks/useCanvas';

const ContextMenu = ({ targetElement, onClose, onCopy, onPaste, position, context = 'canvas', onCustomAction}) => {

  console.log('🔍 ContextMenu context:', context);

  const {
    selectedElements,
    sendForward,
    sendBackward,
    bringToFront,
    sendToBack,
    canMoveForward,
    canMoveBackward,
    getElementLayerPosition,
    deleteElement,
    selectElement,
    getSortedElements,
    copyElements,
    getClipboard,
  } = useCanvas();

  const getOperatingElements = () => {
    if (targetElement) {
      const isTargetSelected = selectedElements.some(el => el.id === targetElement.id);
      if (isTargetSelected && selectedElements.length > 0) {
        // Target is selected, use all selected elements
        return selectedElements;
      } else {
        // Target is not selected, operate only on target
        return [targetElement];
      }
    }
    // No target, use selected elements
    return selectedElements;
  };

  const elements = getOperatingElements();
  const isMultiSelect = elements.length > 1;

  // Don't render if no elements to operate on
  if (elements.length === 0) {
    return null;
  }

  // Check if any selected element can move forward or backward
  const canGoForward = elements.some((el) => canMoveForward(el.id));
  const canGoBackward = elements.some((el) => canMoveBackward(el.id));

  const getDisplayLayerPosition = (element) => {
    if (context === 'timeline') {
      const sorted = getSortedElements();
      const totalLayers = sorted.length;
      const zIndexPosition = sorted.findIndex(el => el.id === element.id);
      return totalLayers - zIndexPosition;
    } else {
      return getElementLayerPosition(element.id);
    }
  };

  const layerPosition = isMultiSelect ? null : getDisplayLayerPosition(elements[0]);

  const handleAction = (action) => {
    if (action === 'replace_media') {
      if (onCustomAction) onCustomAction(action, elements[0]);
      onClose();
      return;
    }

    elements.forEach((element) => {
      switch (action) {
        case 'forward':
          if (canMoveForward(element.id)) sendForward(element.id);
          break;
        case 'backward':
          if (canMoveBackward(element.id)) sendBackward(element.id);
          break;
        case 'front':
          bringToFront(element.id);
          break;
        case 'back':
          sendToBack(element.id);
          break;
        case 'delete':
          deleteElement(element.id);
          break;
        default:
          break;
      }
    });

    // Clear selection after delete
    if (action === 'delete') {
      selectElement(null);
    }

    // Handle copy
    if (action === 'copy') {
      copyElements();
      if (onCopy) onCopy();
    }

    // Handle paste
    if (action === 'paste') {
      if (onPaste) onPaste();
    }

    onClose(); // Close menu after action
  };

  const menuItems = [
    {
      label: context === 'timeline' ? 'Move to Top' : 'Bring to Front',
      action: 'front',
      icon: '⬆️',
      disabled: false,
    },
    {
      label: context === 'timeline' ? 'Move Up' : 'Send Forward',
      action: 'forward',
      icon: '↗️',
      disabled: !canGoForward,
    },
    {
      label: context === 'timeline' ? 'Move Down' : 'Send Backward',
      action: 'backward',
      icon: '↙️',
      disabled: !canGoBackward,
    },
    {
      label: context === 'timeline' ? 'Move to Bottom' : 'Send to Back',
      action: 'back',
      icon: '⬇️',
      disabled: false,
    },
    { type: 'divider' },
    {
      label: 'Replace Media',
      action: 'replace_media',
      icon: '🔄',
      disabled: isMultiSelect || !['video', 'image', 'audio', 'gif'].includes(elements[0]?.type),
    },
    {
      label: 'Copy',
      action: 'copy',
      icon: '📋',
      disabled: false,
      shortcut: 'Ctrl+C',
    },
    {
      label: 'Paste',
      action: 'paste',
      icon: '📋',
      disabled: !getClipboard() || getClipboard().length === 0,
      shortcut: 'Ctrl+V',
    },
    { type: 'divider' },
    {
      label: 'Delete',
      action: 'delete',
      icon: '🗑️',
      disabled: false,
      isDestructive: true,
    },
  ];

  const baseButtonClass =
    'w-full px-2 py-1 text-xs text-left hover:bg-gray-200 hover:text-black transition-colors duration-150 focus:outline-none focus:bg-ag-green focus:text-black cursor-pointer flex items-center gap-2';
  const disabledButtonClass = 'opacity-50 cursor-not-allowed hover:bg-transparent hover:text-white';
  const destructiveButtonClass = 'hover:bg-red-200 focus:bg-red-500';

  return (
    <div
      className="bg-ag-black-800 border border-gray-600 rounded shadow-lg w-64 text-white"
      style={position ? { position: 'absolute', top: position.y, left: position.x } : {}}
    >
      {/* Element info header */}
      <div className="px-2 py-1 text-xs text-gray-400 border-b border-gray-600 truncate">
        {isMultiSelect
          ? `${elements.length} Elements Selected`
          : `${elements[0].name || 'Element'}${layerPosition ? ` (${context === 'timeline' ? 'Track' : 'Layer'} ${layerPosition})` : ''}`}
      </div>

      {/* Menu items */}
      <div className="py-1">
        {menuItems.map((item, index) => {
          if (item.type === 'divider') {
            return <div key={index} className="border-t border-gray-600 my-1" />;
          }

          const buttonClasses = `
            ${baseButtonClass}
            ${item.disabled ? disabledButtonClass : ''}
            ${item.isDestructive && !item.disabled ? destructiveButtonClass : ''}
          `.trim();

          return (
            <button
              key={index}
              onClick={() => !item.disabled && handleAction(item.action)}
              disabled={item.disabled}
              className={buttonClasses}
            >
              <span className="w-4">{item.icon}</span>
              <span className="flex-1">{item.label}</span>
              {item.shortcut && (
                <span className="text-gray-500 text-[10px] ml-4">{item.shortcut}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ContextMenu;